// Phase B sealed-session — withAuthRetry contract change.
//
// Before: token-refresh recovery. withAuthRetry(getAccessToken, execute) ran
// execute(token); on 401 it called getAccessToken({forceRefresh:true}) and
// re-executed with the fresh token.
//
// After: cookie auth owns refresh server-side. The browser cannot mint a
// new session itself, so the recovery on 401 is to send the user through
// /auth/login. withAuthRetry preserves a single-flight gate so concurrent
// 401s don't fire onUnauthorized N times (which would otherwise produce
// N location.assign() racing each other).

export interface WithAuthRetryOpts {
  execute: () => Promise<Response>;
  onUnauthorized: () => void;
}

let inFlight = false;
let inFlightTimer: ReturnType<typeof setTimeout> | null = null;

export async function withAuthRetry(
  opts: WithAuthRetryOpts,
): Promise<Response> {
  const response = await opts.execute();
  if (response.status !== 401) return response;
  if (inFlight) return response;
  inFlight = true;
  try {
    opts.onUnauthorized();
  } finally {
    inFlightTimer = setTimeout(() => {
      inFlight = false;
      inFlightTimer = null;
    }, 1000);
  }
  return response;
}

// Test-only escape hatch — vitest runs share module state.
export function __resetInFlightForTests(): void {
  inFlight = false;
  if (inFlightTimer) {
    clearTimeout(inFlightTimer);
    inFlightTimer = null;
  }
}
